/**
 * B2B login — Supabase-Auth-backed email/password sign-in for firm staff
 * and platform owners.  Consumer patients still use /login (cookie flow).
 *
 * On success, routes to `?next=…` if provided, else `/firm` (staff) or
 * `/admin` (owner).  Ownership is resolved server-side by the require_owner
 * dependency, so we just let the destination guards handle redirection.
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useSupaAuth } from "@/context/SupaAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Buildings, ShieldCheck } from "@phosphor-icons/react";

export default function B2BLogin() {
  const { login } = useSupaAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const nextParam = new URLSearchParams(location.search).get("next");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate(nextParam || "/firm", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#F5F1E8] px-4"
      data-testid="b2b-login-page"
    >
      <Card className="w-full max-w-md p-8 shadow-lg border-[#2C4C3B]/10">
        <div className="flex items-center gap-2 mb-6">
          <Buildings size={28} weight="duotone" className="text-[#2C4C3B]" />
          <h1 className="text-2xl font-semibold text-[#2C4C3B]">
            MedEasy · Firm & Admin
          </h1>
        </div>

        <p className="text-sm text-slate-600 mb-6">
          Sign in with your firm or admin credentials. This is a separate
          login from the consumer app.{" "}
          <Link to="/login" className="text-[#2C4C3B] underline">
            I'm a patient
          </Link>
        </p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="b2b-login-form">
          <div>
            <Label htmlFor="b2b-email">Email</Label>
            <Input
              id="b2b-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="b2b-login-email"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="b2b-password">Password</Label>
            <Input
              id="b2b-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="b2b-login-password"
              className="mt-1"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-[#2C4C3B] hover:bg-[#22392C] text-white"
            disabled={busy}
            data-testid="b2b-login-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#2C4C3B]" />
          Data stored in Canada · RLS-enforced access
        </div>
      </Card>
    </div>
  );
}
